import type { ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getEffectiveBundledWithFlightsOnly, getEffectiveCharterBundleRoute, getOfferDestinationAirport } from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, isRewardFareClass, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import FlashHotelInsufficientLoyaltyPointsCard from './FlashHotelInsufficientLoyaltyPointsCard'
import FlashHotelRewardsAvailableCard from './FlashHotelRewardsAvailableCard'
import OfferPageBNBLcta from '../OfferPageBNBLcta'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { addFlightPrice } from 'lib/offer/pricing'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOfferPackageAddons } from 'actions/AddonActions'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import ContactContext from 'contexts/contactContext'
import Expander from 'components/utils/Expander'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useIntl } from 'react-intl'
import { trackEvent } from 'analytics/analytics'
import { canAddCharterBundleToUserCart } from 'lib/charterFlights/charterBundleOfferPageUtils'

interface Props {
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<OfferPageRoom>
  checkIn?: string
  fetching?: boolean
  isSpoofed: boolean
  isFlightsSelected: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
  bundledWithFlightsOnly: boolean
}

function FlashHotelOptionCTAs(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    isSpoofed,
    isFlightsSelected,
    packageView,
    charterBundleRoute,
    fareClass,
    bundledWithFlightsOnly,
  } = props

  const intl = useIntl()
  const {
    package: pkg,
    flightsAvailable,
    pricing,
  } = packageView
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const { totalFlightPrice: flightPrice, loading: flightPriceLoading, rewardSeatPointsTotal } = useOfferPageFlightPrice()

  const pricingWithFlights = flightPrice ? addFlightPrice(pricing, flightPrice) : undefined

  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || bundledWithFlightsOnly)
  const hasFlightPrice = !!pricingWithFlights
  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Check if user's checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !charterBundleRoute || (!!checkIn && !!charterDepartDate)

  // Only show error when checkIn is selected but flight price fetch failed
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !flightPriceLoading && !!checkIn && isCheckInMatchingCharterDate
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo && !shouldShowCharterFlightErrorMessage && isCheckInMatchingCharterDate

  // Determine if we should use fallback pricing for charter bundle
  // Use fallback when: no checkIn date selected, OR checkIn doesn't match any charter flight date
  const shouldUseFallbackPricing = useMemo(() => {
    if (!charterBundleRoute) return false
    // Use fallback when no checkIn date is selected, or checkIn doesn't match charter flight dates
    return !checkIn || !isCheckInMatchingCharterDate
  }, [charterBundleRoute, checkIn, isCheckInMatchingCharterDate])

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const flightsBundledNoCache = !hasFlightPrice && bundledWithFlightsOnly
  const contact = useContext(ContactContext)
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const isRewardFareSelected = isRewardFareClass(fareClass as FareClassWithReward)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const rewardPointsDeficit = isRewardFareSelected && rewardSeatPointsTotal != null
    ? Math.max(0, rewardSeatPointsTotal - userPoints)
    : 0
  const hasInsufficientPoints = isRewardFareSelected && !!memberId && rewardPointsDeficit > 0

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(pkg)
  const showBNBL = packageView.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && config.accommodation.hasBnbl
  const canAddToUserCart = canBuyNow && canAddCharterBundleToUserCart({
    charterBundleRoute,
    flightOrigin,
  })

  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture
  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      const currentPrice = isFlightsSelected ? pricingWithFlights?.price : pricing.price

      if (currentPrice) {
        trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: isFlightsSelected, packageOptionPrice: currentPrice }))
      }
    } else {
      trackOfferPageEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, isFlightsSelected, offer, pkg.roomRate?.id, pricing.price, pricingWithFlights?.price, trackOfferPageEvent])

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isSpoofed) {
      dispatch(fetchOfferPackageAddons(offer.id, pkg.id))
    }
  }, [dispatch, offer.id, pkg.id, isSpoofed])

  const hotelOnlyBookNowText = rooms.length > 1
    ? intl.formatMessage(
        {
          id: '5dzRfOBflj8W_2IJ9z0n',
          defaultMessage: 'Book {count, plural, one {# room} other {# rooms}}',
          description: 'Button text to book multiple rooms on the hotel or room booking page',
        },
        { count: rooms.length },
      )
    : intl.formatMessage({
        id: 'mpFHvG15p8mwo5wMs8b2',
        defaultMessage: 'Book now',
        description: 'Call-to-action button text to proceed with booking the current offer',
      })

  const checkAvailabilityText = intl.formatMessage({
    id: '_etWrwIdpiw96KvQ_Pzp',
    defaultMessage: 'Check availability',
    description: 'Button text to check availability on the hotel or room booking page',
  })

  const hotelBookButtonText = canBuyNow ? hotelOnlyBookNowText : checkAvailabilityText

  const bookNowText = intl.formatMessage({
    id: 'mpFHvG15p8mwo5wMs8b2',
    defaultMessage: 'Book now',
    description: 'Call-to-action button text to proceed with booking the current offer',
  })

  const flightsBookButtonText = canBuyNow ? bookNowText : checkAvailabilityText

  let ctaContent: ReactNode = null

  if (hasInsufficientPoints) {
    ctaContent = <FlashHotelInsufficientLoyaltyPointsCard rewardPointsDeficit={rewardPointsDeficit} />
  } else if (fetching || (isFlightsSelected || charterBundleRoute) && flightPriceLoading) {
    // WAIT FOR PRICES
    ctaContent = <LoadingIndicator visible floating opaque />
  } else if (shouldShowCharterFlightErrorMessage) {
    // CHARTER BUNDLE MISSING PRICE — only when check-in matches a charter date but price failed
    ctaContent = <MessageBanner kind="critical">
      {intl.formatMessage({
        id: '2frCP-voXkEo2-v8hOji',
        defaultMessage: 'Failed to find charter flight data',
        description: 'Error message when charter flight data cannot be found',
      })}
    </MessageBanner>
  } else if (charterBundleRoute && pricing && shouldUseFallbackPricing) {
    // CHARTER BUNDLE FALLBACK PRICE
    ctaContent = <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      <OfferBookButton
        size="medium"
        type="submit"
        data-testid="HotelBookingBtn"
        disabled={fetching}
        packageView={packageView}
        onSubmit={onSubmit}
      >
        {intl.formatMessage({
          id: 'f6ucrlxn3Ej_isWo_JDQ',
          defaultMessage: 'Check availability',
          description: 'Action button label to check availability on the offer detail page',
        })}
      </OfferBookButton>
      {canAddToUserCart && <Expander expanded={!selectedTargetUpgradePackage}>
        <OfferAddToCartButton
          size="medium"
          fit="flex"
          type="submit"
          disabled={fetching}
          packageView={packageView}
        />
      </Expander>}
      <FlashHotelRewardsAvailableCard />
    </Group>
  } else if ((isFlightsSelected || charterBundleRoute) && flightsEnabled) {
    // FLIGHT PRICE
    ctaContent = <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      {!hasFlightPrice && flightOrigin.code === flightDestination?.code && <TextButton
        size="medium"
        data-filter="airport"
        kind="tertiary"
        onClick={onToggleFilter}
      >
        {intl.formatMessage({
          id: 'FJeKASDKevfTCPbRvSz7',
          defaultMessage: 'Change airport',
          description: 'Button text to change the selected departure or arrival airport in the flight form',
        })}
      </TextButton>}
      {flightsBookable && (!canBuyNow || !!charterBundleRoute) && <OfferBookButton
        size="medium"
        type="submit"
        data-testid="HotelAndFlightsBtn"
        disabled={fetching}
        packageView={packageView}
        data-buytype="flights"
        onSubmit={onSubmit}
      >
        {flightsBookButtonText}
      </OfferBookButton>}
      {flightsBookable && canAddToUserCart && !!charterBundleRoute && <OfferAddToCartButton
        size="medium"
        fit="flex"
        type="submit"
        disabled={fetching}
        packageView={packageView}
        data-buytype="flights"
      />}
      {flightsBookable && (canBuyNow && !charterBundleRoute) && <OfferSelectPackageButton
        size="medium"
        type="submit"
        disabled={fetching}
        packageView={packageView}
        data-buytype="flights"
        isCharterBundle={false}
      />}
      {flightsBundledNoCache &&
        <Group direction="vertical" gap={12} horizontalAlign="center">
          <BodyText variant="small">
            {intl.formatMessage({
              id: 'tGWzY4M0rxJga80jSJHH',
              defaultMessage: 'OR',
              description: 'Text separating booking options in the booking or order summary view',
            })}
          </BodyText>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '_GkJtUt32i73XTGu0SIx',
              defaultMessage: 'Call our concierge team for exclusive prices & priority booking',
              description: 'Text encouraging users to call concierge team on the offer detail page',
            })}
          </BodyText>
          <TextButton
            size="medium"
            kind="secondary"
            startIcon={<LinePhoneIcon />}
            href={`tel:${contact.defaultContact.number}`}
            rel="nofollow"
          >
            {contact.defaultContact.humanReadable}
          </TextButton>
        </Group>}
      <FlashHotelRewardsAvailableCard />
    </Group>
  } else {
    // HOTEL PRICE
    ctaContent = <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      <OfferBookButton
        size="medium"
        type="submit"
        data-testid="HotelBookingBtn"
        disabled={fetching}
        packageView={packageView}
        onSubmit={onSubmit}
      >
        {hotelBookButtonText}
      </OfferBookButton>
      {canAddToUserCart && <Expander expanded={!selectedTargetUpgradePackage}>
        <OfferAddToCartButton
          size="medium"
          fit="flex"
          type="submit"
          disabled={fetching}
          packageView={packageView}
        />
      </Expander>}
    </Group>
  }

  return <>
    {ctaContent}
    {showBNBL && <Expander expanded={!selectedTargetUpgradePackage}>
      <OfferPageBNBLcta packageView={packageView} />
    </Expander>}
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isSpoofed: appState.auth.account.isSpoofed,
  isFlightsSelected: getEffectiveCharterBundleRoute(state) ? true : state.flights, // enforce to be true when charter bundle is selected to avoid unexpected behavior
  open: !!state.activeFilter,
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  flightDestination: getOfferDestinationAirport(state, appState),
  fareClass: state.fareClass,
  bundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
}), FlashHotelOptionCTAs)
